import { ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from '@/components/ui/card';
import { IP_NOT_ALLOWED_MESSAGE } from '@/services/httpError';

interface AccessDeniedPageProps {
	/** Correlation id of the refused request, for support. */
	requestId?: string;
	onRetry: () => void;
	onSignOut: () => void;
}

/**
 * Shown instead of the CMS when the workspace's IP allowlist refuses the
 * current network (403 IP_NOT_ALLOWED). The session is kept: switching to an
 * approved network and retrying continues where the user was.
 */
export function AccessDeniedPage({
	requestId,
	onRetry,
	onSignOut,
}: AccessDeniedPageProps) {
	return (
		<main className="flex min-h-dvh items-center justify-center p-4">
			<Card className="w-full max-w-md">
				<CardHeader>
					<div className="bg-destructive/10 text-destructive mb-2 flex size-10 items-center justify-center rounded-full">
						<ShieldAlert className="size-5" aria-hidden="true" />
					</div>
					<CardTitle
						className="text-xl tracking-tight"
						role="heading"
						aria-level={1}
					>
						Access denied from this network
					</CardTitle>
					<CardDescription>{IP_NOT_ALLOWED_MESSAGE}</CardDescription>
				</CardHeader>
				{requestId ? (
					<CardContent className="text-muted-foreground text-sm">
						Reference for your administrator:{' '}
						<code className="bg-muted rounded px-1 py-0.5 text-xs break-all">
							{requestId}
						</code>
					</CardContent>
				) : null}
				<CardFooter className="flex flex-wrap gap-2">
					<Button type="button" onClick={onRetry}>
						Try again
					</Button>
					<Button type="button" variant="outline" onClick={onSignOut}>
						Sign out
					</Button>
				</CardFooter>
			</Card>
		</main>
	);
}
